/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import isEmpty from 'lodash.isempty';
import isEqual from 'lodash.isequal';
import isUndefined from 'lodash.isundefined';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {getAlertsFired} from '@ion/core/src/services/Alerts';
import {
  getApplications,
  getCrashEvents,
  getCSVUrl,
  getFile,
  getFileUrl,
  getReleaseNote,
  getVinData,
  loadAll,
  loadAllAccounts,
  loadAllAlertNotification,
  loadAllApplicationIds,
  loadAllAssets,
  loadAllDevicePartNumbers,
  loadAllDevicesStatus,
  loadAllDevicesUsingSmartSearch,
  loadAllDownloadJobs,
  loadAllEventHistory,
  loadAllGroups,
  loadAllInventoryDevices,
  loadAllJobDevices,
  loadAllNotifications,
  loadAllNotificationType,
  loadAllRoles,
  loadAllUsers,
  loadDownloadDevicesJobs,
  loadJobStatus,
  loadPulsDevicesBySmartSearch,
  searchDVR,
  searchVideoEvents
} from '@ion/core/src/services/Connect';
import {parseDevices, parseDownloadJobs} from '@ion/core/src/services/ConnectUtils';
import {hasErrorCodes} from '@ion/core/src/services/JobMetaDefinitions';
import {JobDetails} from '@ion/core/src/types/editors';
import {
  Asset,
  CTCEntity,
  DownloadFileTypes,
  GenericObject,
  Roles
} from '@ion/core/src/types/entities';
import {LoadDevicesStatusParams, LoadGroupsParams} from '@ion/core/src/types/services';
import {DownloadTemplate} from '@ion/core/src/types/templates';
import {sortBy} from '@ion/core/src/utils/ArrayUtil';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import {getHeartBeat} from '@ion/core/src/utils/DeviceUtils';
import {capitalizeFirstLetter} from '@ion/core/src/utils/StringUtil';
import {exportXLSX} from '@ion/core/src/xlsx/exporter';
import {downloadBlankTemplate} from '@ion/core/src/xlsx/TemplateHelpers';
import {
  deviceValueResolver,
  ExportClient,
  exportCSV,
  ExportNonPaginatedClient,
  ExportXLSType,
  generateRows,
  paging
} from '@ion/dls/src/grid/Exporter';
import getGlobalProp from '@ion/dls/src/props/GlobalPropsConfig';
import {
  createSortParams,
  downloadBlob,
  getAccountId,
  getAllAccountIds,
  hasExtensions,
  hasExtensionsKey
} from '../../helpers';
import {getCurrentUser} from '../../pages/admin/users/useAuthenticatedUser';
import {
  createAncestorList,
  getBatchIds,
  getDeviceDetails,
  getHeadersForTab,
  getHeartBeatInfo,
  getICNHeartBeatData,
  getJobDetails,
  getTaskDetails,
  getUpdatedGroups
} from '../../pages/devices/helpers';
import {createSortAndSearchQueryParameters} from '../../pages/devices/ModularSearchUtils';
import {getVin} from '../../pages/devices/state';
import {getConfigVersion} from '../../pages/devices/ViewDeviceEditorUtils';
import {noFileMessage} from '../../prompts/constants';
import {loadAllData} from '../../store/helpers';
import {ExportContext} from '../../types/redux';
import {toggleUserPrompt} from './user-prompt';

export const EXPORT_LIMIT = 100;

enum FileTypes {
  DEVICE_FILE = 'device file',
  RELEASE_NOTE = 'release notes'
};

/**
 * defines the shape of the report export paramaters which are generally used for
 * export the report grid.
 */
export interface ExportReport extends ExportClient {
  items: CTCEntity[];
  title?: string;
  subTitle?: string;
  augmenterArgs?: any[];
}

/**
 * defines the parameter structure for @function performExport
 */
export interface PerformExportParams {
  items: any[];
  exportItems: Function;
  dispatch: Function;
  entity: string;
  forceToExport?: boolean;
};

// invoked when an export is initiated ...
export const EXPORT_REQUEST   = 'EXPORT_REQUEST';

// invoked when an export has completed ...
export const EXPORT_RESPONSE  = 'EXPORT_RESPONSE';

// invoked when an export has failed ...
export const EXPORT_ERROR     = 'EXPORT_ERROR';

// invoked when the user has been notified of an initiated export ...
export const EXPORT_CONFIRMED = 'EXPORT_CONFIRMED';

// invoked when the user has been notified of a completed export ...
export const EXPORT_COMPLETED = 'EXPORT_COMPLETED';

/**
 * General utility which helps in downloading templates as excel with pre-populated
 * values specific to current CTC Entity type.
 */
export const exportXLS = ({
  entity,
  items,
  columns,
  augmenter,
  augmenterArgs,
  params
}: ExportXLSType) => {
  const rows: CTCEntity[] = generateRows({
    entity,
    items,
    columns,
    augmenter,
    augmenterArgs
  });
  params.rows = rows;
  downloadBlankTemplate({...params});
};

/**
 * Performs the export operation if there are items to export.
 *
 * If the `items` array is not empty, it calls the `exportItems` function to initiate the export process.
 * If the `items` array is empty, it dispatches an error action with a message indicating that no data is available
 * and prompts the user to change selections.
 *
 * ```typescript
 *
 *  import {performExport} from '@ion/applications/src/store/actions/exporter';
 *
 *  performExport({
 *    exportItems: () => {
 *       // performs exports...
 *    },
 *    dispatch,
 *    items,
 *    entity
 *  });
 *
 * ```
 *
 * @param items - The list of CTC items to be exported.
 * @param exportItems - A callbackfunction to handle the export process.
 * @param dispatch - The Redux dispatch function for dispatching actions.
 * @param entity - The context or entity related to the export operation.
 */
export const performExport = ({
  items,
  exportItems,
  dispatch,
  entity,
  forceToExport = false
}: PerformExportParams): void => {
  if (items?.length || forceToExport) {
    exportItems();
    return;
  }
  dispatch({type: EXPORT_ERROR, entity, message: translate('common:export.noData')});
};

/**
 * Provides the Users Export Action Creator which manages retrieving all users
 * and converting the retrieved users to a CSV for subsequent download.
 */
export const exportUsers = (
  {columns, augmenter, items, ...rest}: ExportClient,
  downloadParams?: DownloadTemplate) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.user;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      const accountId              = getAccountId(getState);
      const users                  = (
        !items?.length
          ? await loadAllUsers({accountId, ...rest, ...paging})
          : items
      );
      const roles                  = getState().adminUsers.roles;
      const augmenterArgs: [Roles] = [roles];
      const exportParams           = {entity, items: users, columns, augmenter, augmenterArgs};
      if (!downloadParams) {
        exportCSV({...exportParams, includeTimestamp: true});
      } else {
        exportXLS({...exportParams, params: downloadParams});
      }
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Asset Export Action Creator which manages retrieving all assets
 * and converting the retrieved assets to a CSV for subsequent download.
 */
export const exportAssets = (
  {columns, augmenter, items, areTotalRowsSelected, pageSize, gridExportCount, ...rest}: ExportClient,
  downloadParams?: DownloadTemplate,
  hasExportLimit?: boolean) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.asset;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      const accountId    = getAccountId(getState);
      const assets       = (
        !items?.length
          ? await loadAllAssets({accountId, ...rest, ...paging})
          : ((areTotalRowsSelected && gridExportCount > pageSize) && hasExportLimit)
            ? await loadAllAssets({accountId, ...rest, ...paging}) : items
      );
      const exportParams = {entity, items: assets, columns, augmenter};
      if (!downloadParams) {
        exportCSV({...exportParams, includeTimestamp: true});
      } else {
        exportXLS({...exportParams, params: downloadParams});
      }
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Device Export Action Creator which manages retrieving all devices
 * and converting the retrieved devices to a CSV for subsequent download.
 */
export const exportDevices = ({
  columns,
  augmenter,
  items,
  areTotalRowsSelected,
  pageSize,
  gridExportCount,
  sortOn,
  requestValidations,
  ...rest
}: ExportClient,
downloadParams?: DownloadTemplate,
isCtcAdmin?: boolean,
hasExportLimit?: boolean,
platformResults?: GenericObject[],
/* eslint-disable-next-line max-params */
columnKeys?: string[],
hasVehicleProfile?: boolean,
hasInheritableGroups?: boolean,
viewInheritableGroup?: boolean,
isVbuFwEnabled?: boolean,
isHeartBeat?: boolean,
includeSubGroups?: boolean
  /* eslint-disable-next-line max-params */
) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.device;
    const path   = 'admin-devices:grid.vehicleProfile';

    dispatch({type: EXPORT_REQUEST, entity});

    const isValidationEnabled = getGlobalProp('grids.export.validation.enabled', false) as boolean;
    const {valid}             = requestValidations;

    try {
      const accountId = getAccountId(getState);
      const {
        devices: {
          search
        }
      } = getState();

      const getDevices = async() => {
        let includeSortParams = false;
        let includeParams     = false;

        if (search?.any || search?.mdt || search?.lmu || hasExtensions(sortOn)) {
          includeSortParams = true;
          includeParams     = true;
        } if (sortOn?.query?.sort?.some?.(hasExtensionsKey)) {
          includeSortParams = false;
          includeParams     = false;
        } if (sortOn?.query?.sort?.some?.(item => 'extensions.fileVersions.version' in item)) {
          includeSortParams = false;
          includeParams     = false;
        }

        const data = {
          accountId,
          includeSortParams,
          includeParams,
          ...rest,
          ...paging,
          usePulse              : true,
          includeAccountFilter  : true,
          includeDeviceSettings : false,
          includeGroups         : true
        } as any;

        if (sortOn?.[0]?.includes('lastReportedTime')) {
          sortOn[0] = 'name';
        }
        if (sortOn && !includeSortParams) {
          data.sortOn = sortOn;
        } if (includeSortParams && includeParams) {
          if (search?.any && sortOn?.query?.sort) {
            sortOn = Object.keys(sortOn.query.sort?.[0]);
          }
          data.sortOn = createSortParams({
            item        : sortOn?.[0],
            id          : getAccountId(getState),
            queryParams : createSortAndSearchQueryParameters(search, isVbuFwEnabled),
            columnKeys,
            isVbuFwEnabled
          });
        } if (rest?.maxSearch) {
          data.sortOn = sortOn;
        }
        const devices              = valid ? await loadAllDevicesUsingSmartSearch(data) : [];
        const user                 = getCurrentUser(getState);
        const applications         = user?.applications;
        const deviceswithHeartBeat = [];
        const deviceswithVin = [];
        let updatedGroups    = null;
        let ancestorsGroups  = null;
        if (isHeartBeat) {
          const heartBeatData    = getICNHeartBeatData(devices, applications);
          const heartBeatPayload = getHeartBeatInfo(heartBeatData, accountId);
        
          if (heartBeatPayload) {
            const heartBeatInfo = await getHeartBeat(heartBeatPayload, includeSubGroups);
        
            if (!isEmpty(heartBeatInfo?.results?.devicecheckinmodel?.devicecheckindata)) {
              const deviceHeartBeat = heartBeatInfo?.results?.devicecheckinmodel?.devicecheckindata;
              devices?.forEach(item => {
                const heartBeatResult = deviceHeartBeat.find(device => {
                  return isEqual(device.deviceId, item.esn);
                });
                deviceswithHeartBeat.push({...item, heartBeatInfo: heartBeatResult || {}});
              });
            }
          }
        }
        if (hasVehicleProfile) {
          const vinItems = devices?.map((item) => getVin(item))?.filter(e => typeof e === 'string' && e !== '')?.map(i => `vin=${i}`).join('&');
          if (vinItems?.length !== 0) {
            try {
              const vinInfo           = await getVinData(vinItems);
              const {results, errors} = vinInfo?.data?.response || {};
              const checkItems        = deviceswithHeartBeat?.length ? deviceswithHeartBeat : devices;
              checkItems?.forEach(item => {
                let getVinValue;
                const vinItem        = getVin(item);
                const vinItemsResult = results.find(device => isEqual(vinItem, device?.vin));
                const vinItemsErrors = errors?.find(device => isEqual(vinItem, device?.error?.vin));
                const {isTestVin}    = vinItemsResult || {};
                const {description}  = vinItemsErrors?.error || {};
                if (!isUndefined(isTestVin)) {
                  getVinValue = isTestVin
                    ? translate(`${path}.test`)
                    : translate(`${path}.production`);
                } else if (description === 'VIN not found') {
                  getVinValue = translate(`${path}.production`);
                }
                deviceswithVin.push({...item, vinInfo: getVinValue});
              });
            } catch (err) {
              error(`An Error occurred while retrieving the Vehicle Profiles for the following VINS: ${vinItems}`);
            }
          }
        }

        if (hasInheritableGroups && viewInheritableGroup) {
          try {
            const {currentBatch, batches} = getBatchIds(devices);
  
            if (currentBatch) {
              batches.push(currentBatch?.slice(0, -1));
            } if (batches?.length) {
              try {
                const inheritableGroups = (await Promise.all(batches?.map(async(item) => {
                  const data: LoadGroupsParams = {
                    filterOn : {id: item},
                    pageId   : 1,
                    pageSize : 100
                  };
                  try {
                    const result = await loadAllGroups(data);
                    return result;
                  } catch (err) {
                    error(err);
                  }
                })))?.flat();

                try {
                  ancestorsGroups = await createAncestorList(accountId);
                } catch (err) {
                  error(err);
                }
  
                updatedGroups = getUpdatedGroups(devices, inheritableGroups, ancestorsGroups);
              } catch (err) {
                error(err);
              }
            }
          } catch (err) {
            error(err);
          }
        }

        let items = devices;
        if (deviceswithVin?.length) {
          if (updatedGroups?.length) {
            items = deviceswithVin.map((vinItem, index) => {
              const groupItem = updatedGroups[index];
              return {...vinItem, ...groupItem};
            });
          } else {
            items = deviceswithVin;
          }
        } else if (deviceswithHeartBeat?.length) {
          if (updatedGroups?.length) {
            items = deviceswithHeartBeat.map((devicewithHeartBeat, index) => {
              const groupItem = updatedGroups[index];
              return {...devicewithHeartBeat, ...groupItem};
            });
          } else {
            items = deviceswithHeartBeat;
          }
        } else if (updatedGroups?.length) {
          items = updatedGroups;
        }
        return items;
      };

      //displaying vinInfo value as Production only while exporting
      const new_updated_data = items?.map((item) => { // 3640
        if (!isEqual(item?.vinUI, '') && isUndefined(item?.vinInfo)) {
          return {
            ...item,
            vinInfo : translate(`${path}.production`)
          };
        }
        return item;
      });

      let devices = [];
      const isSelectionCount = (areTotalRowsSelected && gridExportCount > pageSize) && hasExportLimit;
      if (hasVehicleProfile && !isSelectionCount) {
        devices = new_updated_data;
      } else if (!items?.length || isSelectionCount) {
        devices = await getDevices();
      } else {
        devices = sortBy(items, 'id');
      }

      performExport({
        exportItems : () => {
          const augmenterArgs: [GenericObject[]] = [platformResults];
          const exportParams = {
            items         : devices,
            valueResolver : deviceValueResolver,
            entity,
            columns,
            augmenter,
            augmenterArgs
          };
          if (!downloadParams) {
            exportCSV({...exportParams, includeTimestamp: true});
          } else {
            exportXLS({...exportParams, params: downloadParams});
          }
          dispatch({type: EXPORT_RESPONSE, entity});
        },
        forceToExport : !isValidationEnabled,
        items         : devices,
        entity,
        dispatch
      });
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Exports the given list of devices & columns into a CSV.
 */
export const exportSmartSearchDevices = ({columns, augmenter, items, ...rest}: ExportClient) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.device;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      const accountId = getAccountId(getState);
      const {devices} = (
        !items?.length
          ? await loadPulsDevicesBySmartSearch({
            accountId,
            includeGroups    : true,
            includeSubGroups : rest[rest.length - 1],
            ...rest,
            ...paging
          })
          : items
      );
      const list = devices.map(device => ({
        ...device,
        firmwareVersion : device?.extensions?.firmwareVersion,
        configVersion   : getConfigVersion(device, true)
      }));
      exportCSV({entity, items: list, columns, augmenter, includeTimestamp: true});
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

export const exportNotificationAlert = ({columns, augmenter, items, ...rest}: ExportClient) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.notification;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      const accountId = getAccountId(getState);
      const notifications = (
        !items?.length
          ? await loadAllAlertNotification({accountId, ...rest, ...paging})
          : items
      );
      exportCSV({
        augmenterArgs    : [true],
        includeTimestamp : true,
        items            : notifications,
        entity,
        columns,
        augmenter
      });
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

export const exportDownloadJobs = ({columns, augmenter, items, ...rest}: ExportClient) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.downloadJobs;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      const accountId = getAccountId(getState);
      const downloadJobs = (
        !items?.length
          ? await loadAllDownloadJobs({accountId, ...rest, ...paging})
          : items
      );
      exportCSV({
        augmenterArgs    : [true],
        includeTimestamp : true,
        items            : downloadJobs,
        entity,
        columns,
        augmenter
      });
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

export const exportDownloadJobsStatus = ({columns, augmenter}: ExportClient, downloadjobs) => {
  return async(dispatch: Function): Promise<any> => {
    const entity = ExportContext.downloadJobs;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      const response: any = await loadDownloadDevicesJobs(downloadjobs);
      response?.items?.forEach(tasks => {
        const result   = parseDownloadJobs(tasks);
        response.items = result;
        response.count = result.length;
        exportCSV({
          items            : response.items,
          augmenterArgs    : [true],
          includeTimestamp : true,
          entity,
          columns,
          augmenter
        });
      });
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

export const exportDevicesStatus = (
  {columns, augmenter, items, areTotalRowsSelected, gridExportCount}: ExportClient,
  asset: Asset,
  {index, page, pageSize}
) => {
  return async(dispatch: Function): Promise<any> => {
    const entity = ExportContext.downloadJobs;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      const data: LoadDevicesStatusParams = {
        accountId : asset?.id,
        index,
        ...paging,
        pageSize  : pageSize || paging.pageSize
      };
      const getAllDeviceStatus = async() => {
        return loadAllDevicesStatus(data);
      };
      const result = (!items?.length || (areTotalRowsSelected && gridExportCount > pageSize)
        ? await getAllDeviceStatus()
        : items
      );
      exportCSV({
        items            : parseDevices(result),
        augmenterArgs    : [true],
        includeTimestamp : true,
        entity,
        columns,
        augmenter
      });
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

export const exportDownloadFile = (exportFile, fileType) => {
  return async(dispatch: Function): Promise<any> => {
    const entity = ExportContext.deviceFiles;
    try {
      const {id}           = exportFile || {};
      const fileResult     = await getFile(id);
      const downloadCSV    = !isEmpty(fileType) && isEqual(fileType, DownloadFileTypes.CSV);
      const fileURL        = downloadCSV ? await getCSVUrl(id) : await getFileUrl(id);
      const isDownloadable = fileURL[0]?.signedURL || fileURL[0]?.singedURL;
      const filename       = fileResult[0]?.name;
      const name           = downloadCSV ? `${filename.substr(0, filename.lastIndexOf('.'))}.csv`: filename;
      const onNotFound     = () => dispatch(toggleUserPrompt(noFileMessage(FileTypes.DEVICE_FILE)));
      if (isDownloadable) {
        downloadBlob(isDownloadable, name, onNotFound);
      } else {
        dispatch({type: EXPORT_ERROR, entity});
      }
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

export const exportReleaseNotes = (exportReleaseNote) => {
  return async(dispatch: Function): Promise<any> => {
    const entity = ExportContext.deviceFiles;
    try {
      const {id}              = exportReleaseNote || {};
      const releaseNoteResult = await getFile(id);
      const releaseNoteURL    = await getReleaseNote(id);
      const isDownloadable    = releaseNoteURL[0]?.signedURL || releaseNoteURL[0]?.singedURL;
      const onNotFound        = () => dispatch(toggleUserPrompt(noFileMessage(FileTypes.RELEASE_NOTE)));
      const hasReleaseNotes   = releaseNoteResult[0]?.validationMetaData?.releaseNotesName;
      const releaseNotesName  = hasReleaseNotes ?? `${releaseNoteResult[0]?.name}.notes`;
      downloadBlob(isDownloadable, releaseNotesName, onNotFound);
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

export const exportDownloadJob = ({columns}: ExportReport, exportJobs) => {
  return async(dispatch: Function): Promise<any> => {
    const entity = ExportContext.downloadJobs;
    dispatch({type: EXPORT_REQUEST, entity});

    const data = await loadAllData({
      api  : loadAllDevicesStatus,
      jobs : exportJobs
    });
    try {
      const taskStatuses    = parseDownloadJobs(exportJobs);
      const devicesStatuses = data;
      const mergeStatuses = (taskStatuses, devicesStatuses) => {
        const resolve = (prop: string, item, appId?: string) => (prop === 'errorCodes' ? hasErrorCodes(item?.[prop], appId) : item?.[prop]) || '-';
        const results = [];
        taskStatuses?.forEach((taskStatus, index) => {
          const deviceStatuses = devicesStatuses?.[index];
          deviceStatuses?.forEach(deviceStatus => {
            results.push({
              task           : resolve('task', taskStatus),
              description    : resolve('description', taskStatus),
              progressStatus : resolve('progressStatus', taskStatus),
              total          : resolve('total', taskStatus),
              completed      : resolve('completed', taskStatus),
              pending        : resolve('pending', taskStatus),
              failed         : resolve('failed', taskStatus),
              cancelled      : resolve('cancelled', taskStatus),
              esn            : resolve('esn', deviceStatus?.device),
              typeName       : resolve('title', deviceStatus?.device?.type),
              updateTime     : resolve('updateTime', deviceStatus?.taskStatus),
              errorCodes     : resolve('errorCodes', deviceStatus?.taskStatus, deviceStatus?.device?.extensions?.appId),
              status         : resolve('status', deviceStatus?.taskStatus)
            });
          });
        });
        return results;
      };
      const items = mergeStatuses(taskStatuses, devicesStatuses);
      exportCSV({
        columns,
        entity,
        items,
        includeTimestamp : true
      });
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Device Export Action Creator which manages retrieving all JobStatus
 * and converting the retrieved JobStatus to a CSV for subsequent download.
 */
export const exportJobStatus = ({columns, augmenter, ...rest}: ExportClient) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.jobStatus;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      const accountId   = getAccountId(getState);
      const result: any = await loadJobStatus({accountId, ...rest, ...paging});
      exportCSV({
        items            : result.items,
        augmenterArgs    : [true],
        includeTimestamp : true,
        entity,
        columns,
        augmenter
      });
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Task Export Action Creator which manages retrieving all tasks
 * and converting the retrieved tasks to a CSV for subsequent download.
 */
export const exportTasks = ({columns, items, sortOn}: ExportNonPaginatedClient) => {
  return async(dispatch: Function): Promise<any> => {
    const entity = ExportContext.jobStatus;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      exportCSV({
        includeTimestamp : true,
        entity,
        items,
        columns,
        sortOn
      });
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Exports a CSV with the given row data.
 */
export const exportTaskStatus = ({columns, augmenter}: ExportClient, rows) => {
  return async(dispatch: Function): Promise<any> => {
    const entity = ExportContext.taskStatus;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      exportCSV({
        items            : rows,
        augmenterArgs    : [true],
        includeTimestamp : true,
        entity,
        columns,
        augmenter
      });
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Exports a CSV with the given row data.
 */
export const exportApplicationIds = ({columns, augmenter, items, ...rest}: ExportClient, rows) => {
  return async(dispatch: Function): Promise<any> => {
    const entity = ExportContext.applicationIds;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      const applicationIds = await loadAllApplicationIds({...rest, ...paging});
      exportCSV({
        items            : applicationIds,
        augmenterArgs    : [true],
        includeTimestamp : true,
        entity,
        columns,
        augmenter
      });
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Exports a CSV with the given row data.
 */
export const exportDevicePartNumbers = ({columns, augmenter, items, ...rest}: ExportClient, rows) => {
  return async(dispatch: Function): Promise<any> => {
    const entity = ExportContext.devicePartNumbers;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      let devicePartNumbers;
      if (!isEmpty(rest?.filterOn)) {
        devicePartNumbers = await loadAllDevicePartNumbers({...rest, ...paging});
      } else {
        devicePartNumbers = rows;
      }
      exportCSV({
        items            : devicePartNumbers,
        augmenterArgs    : [true],
        includeTimestamp : true,
        entity,
        columns,
        augmenter
      });
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Notification Types Export Action Creator which manages retrieving all Notification Types
 * and converting the retrieved users to a CSV for subsequent download.
 */
export const exportNotificationType = ({columns, augmenter, items, ...rest}: ExportClient) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.notification;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      const accountId = getAccountId(getState);
      const notificationType = (
        !items?.length
          ? await loadAllNotificationType({accountId, ...rest, ...paging})
          : items
      );
      exportCSV({
        augmenterArgs    : [true],
        includeTimestamp : true,
        items            : notificationType,
        entity,
        columns,
        augmenter
      });
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Notifications Export Action Creator which manages passed
 * all Notifications Items and converting the retrieved Items to a CSV for subsequent
 * download.
 */
export const exportNotificationPanel = (
  {columns}: ExportReport) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.notification;
    dispatch({type: EXPORT_REQUEST, entity});
    const {
      globalGroup: {
        globalGroupId
      }
    } = getState();
    try {
      const response = await getAlertsFired({
        accountIds : getAllAccountIds(getState, globalGroupId)
      });
      const notifications = response?.map(result => {
        const {alertType, createdOn, alertActions, address} = result || {};
        return {
          ...result,
          alertActions : String(alertActions?.map(({alertAction}) => alertAction?.title)),
          alertType    : alertType?.title,
          createdOn    : formatDateTime(createdOn),
          address      : address?.label || null
        };
      });
      exportCSV({
        columns,
        entity,
        items            : notifications,
        includeTimestamp : true
      });
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
* Provides the Groups Export Action Creator which manages retrieving all groups
* and converting the retrieved groups to a CSV for subsequent download.
*/
export const exportGroups = (
  {columns, augmenter, includeSubGroups, items, includeParent = false, ...rest}: ExportClient,
  downloadParams?: DownloadTemplate) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.account;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      const parentAccountId = getAccountId(getState);
      const subAccountIds   = includeSubGroups
        ? getAllAccountIds(getState, parentAccountId, includeParent).toString()
        : null;
      const accounts = (
        !items?.length
          ? await loadAllAccounts({subAccountIds, parentAccountId, includeSubGroups, ...rest, ...paging})
          : items
      );
      const augmenterArgs: [any] = [true];
      const exportParams = {entity, items: accounts, columns, augmenter, augmenterArgs};
      if (!downloadParams) {
        exportCSV({...exportParams, includeTimestamp: true});
      } else {
        exportXLS({...exportParams, params: downloadParams});
      }
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Notification Export Action Creator which manages retrieving all Notifications
 * and converting the retrieved Notifications to a CSV for subsequent download.
 */
export const exportNotifications = ({columns, augmenter, requestValidations, items, ...rest}: ExportClient) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.notification;
    dispatch({type: EXPORT_REQUEST, entity});
    const isValidationEnabled = getGlobalProp('grids.export.validation.enabled', false) as boolean;
    const {valid} = requestValidations;
    try {
      const accountId = getAccountId(getState);
      const notifications = valid ? (
        !items?.length
          ? await loadAllNotifications({accountId, ...rest, ...paging})
          : items
      ) : [];
      performExport({
        exportItems : () => {
          exportCSV({
            augmenterArgs    : [true],
            includeTimestamp : true,
            items            : notifications,
            entity,
            columns,
            augmenter
          });
          dispatch({type: EXPORT_RESPONSE, entity});
        },
        forceToExport : !isValidationEnabled,
        items         : notifications,
        entity,
        dispatch
      });
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Roles Export Action Creator which manages retrieving all roles
 * and converting the retrieved roles to a CSV for subsequent download.
 */
export const exportRoles = ({
  columns,
  augmenter,
  items,
  requestValidations,
  ...rest
}: ExportClient,
isCtcAdmin = false,
applicationKeys = null
) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.role;
    dispatch({type: EXPORT_REQUEST, entity});
    const isValidationEnabled = getGlobalProp('grids.export.validation.enabled', false) as boolean;
    const {valid} = requestValidations;
    try {
      const accountId       = getAccountId(getState);
      if (isCtcAdmin && valid) {
        const applications    = await getApplications(accountId);
        applicationKeys = applications?.map(({applicationKey}) => applicationKey).join(', ');
      }
      const roles = valid ? (
        !items?.length
          ? await loadAllRoles({accountId, applicationKeys, ...rest, ...paging})
          : items
      ) : [];
      performExport({
        exportItems : () => {
          exportCSV({
            includeTimestamp : true,
            items            : roles,
            entity,
            columns,
            augmenter
          });
          dispatch({type: EXPORT_RESPONSE, entity});
        },
        forceToExport : !isValidationEnabled,
        items         : roles,
        dispatch,
        entity
      });
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Groups Export Action Creator which manages retrieving all groups
 * and converting the retrieved groups to a CSV for subsequent download.
 */
export const exportCTCAdminGroups = ({columns, augmenter, items, ...rest}: ExportClient) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.groups;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      const accountId = getAccountId(getState);
      const groups = (
        !items?.length
          ? await loadAllGroups({accountId, ...rest, ...paging})
          : items
      );
      exportCSV({
        augmenterArgs    : [true],
        includeTimestamp : true,
        items            : groups,
        entity,
        columns,
        augmenter
      });
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Asset History Export Action Creator which manages passed
 * all History Items and converting the retrieved Items to a CSV for subsequent
 * download.
 */
export const exportAssetHistory = (
  {columns, augmenter, items, title, subTitle}: ExportReport) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.assetHistory;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      exportCSV({
        includeTimestamp : true,
        entity,
        columns,
        augmenter,
        items,
        title,
        subTitle
      });
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Event History Export Action Creator which manages passed
 * all History Items and converting the retrieved Items to a CSV for subsequent
 * download.
 */
export const exportEventHistory = ({columns, augmenter, ...rest}: ExportClient, searchParams) => {
  return async(dispatch: Function): Promise<any> => {
    const entity = ExportContext.eventHistory;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      const events = await loadAllEventHistory({...searchParams, ...rest, ...paging});
      const items  = events?.map(event => {
        const [key, value] = Object.entries(event)[0];
        return {
          ...value,
          eventNameUI : capitalizeFirstLetter(key),
          eventTimeUI : formatDateTime(value?.eventTime)
        };
      });
      exportCSV({
        augmenterArgs    : [true],
        includeTimestamp : true,
        items            : events,
        entity,
        columns,
        augmenter
      });
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Maintenance History Export Action Creator which manages retrieving
 * all History Items and converting the retrieved Items to a CSV for subsequent
 * download.
 */
export const exportDevicePassword = ({columns, items}: ExportNonPaginatedClient) => {
  return async(dispatch: Function): Promise<any> => {
    const entity = ExportContext.devicePassword;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      exportCSV({
        includeTimestamp : true,
        entity,
        items,
        columns
      });
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Moved Devices Export Action Creator which manages retrieving
 * all Moved Items and converting the retrieved Items to a CSV for subsequent
 * download.
 */
export const exportMovedDevices = ({columns, items}: ExportNonPaginatedClient) => {
  return async(dispatch: Function): Promise<any> => {
    const entity = ExportContext.movedDevices;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      exportCSV({
        includeTimestamp : true,
        entity,
        items,
        columns
      });
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Moved Assets Export Action Creator which manages retrieving
 * all Moved Items and converting the retrieved Items to a CSV for subsequent
 * download.
 */
export const exportMovedAssets = ({columns, items}: ExportNonPaginatedClient) => {
  return async(dispatch: Function): Promise<any> => {
    const entity = ExportContext.movedAssets;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      exportCSV({
        includeTimestamp : true,
        entity,
        items,
        columns
      });
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Crash events Export Action Creator which manages retrieving all crash
 * events and converting the retrieved events to a CSV for subsequent download.
 */
export const exportCrashEvents = ({columns, augmenter, requestValidations, augmenterArgs, ...rest}: ExportClient) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.crashboxx;
    dispatch({type: EXPORT_REQUEST, entity});
    const {valid} = requestValidations;
    try {
      const accountId   = getAccountId(getState);
      const crashEvents = valid ? await loadAll(
        getCrashEvents,
        {accountId, ...rest, ...paging},
        'crashEvents',
        true
      ) : [];
      performExport({
        exportItems : () => {
          exportCSV({
            items            : crashEvents,
            includeTimestamp : true,
            augmenterArgs,
            entity,
            columns,
            augmenter
          });
          dispatch({type: EXPORT_RESPONSE, entity});
        },
        items : crashEvents,
        entity,
        dispatch
      });
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the all video events Export Action Creator which manages retrieving all video
 * events and converting the retrieved events to a CSV for subsequent download.
 */
export const exportVideoEvents = ({
  columns,
  augmenter,
  augmenterArgs,
  includeSubGroups,
  filterOn,
  requestValidations,
  ...rest
}: ExportClient) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.videoEvents;
    dispatch({type: EXPORT_REQUEST, entity});
    const {valid} = requestValidations;
    try {
      const videoEvents = valid ? await loadAll(
        searchVideoEvents,
        {...paging, ...filterOn},
        'videoEvents'
      ) : [];
      performExport({
        exportItems : () => {
          exportCSV({
            items            : videoEvents,
            includeTimestamp : true,
            augmenterArgs,
            entity,
            columns,
            augmenter
          });
          dispatch({type: EXPORT_RESPONSE, entity});
        },
        items : videoEvents,
        entity,
        dispatch
      });
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the all vision request Export Action Creator which manages retrieving all dvr
 * request and converting the retrieved events to a CSV for subsequent download.
 */
export const exportDVRRequests = ({
  columns,
  augmenter,
  augmenterArgs,
  includeSubGroups,
  filterOn,
  requestValidations,
  ...rest
}: ExportClient) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.visionRequests;
    dispatch({type: EXPORT_REQUEST, entity});
    const {valid} = requestValidations;
    try {
      const requests = valid ? await loadAll(
        searchDVR,
        {...paging, ...filterOn},
        'dvrRequests'
      ) : [];
      performExport({
        exportItems : () => {
          exportCSV({
            items            : requests,
            includeTimestamp : true,
            augmenterArgs,
            entity,
            columns,
            augmenter
          });
          dispatch({type: EXPORT_RESPONSE, entity});
        },
        items : requests,
        entity,
        dispatch
      });
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Inventory Devices Export Action Creator which manages retrieving
 * all Devices and converting the retrieved Items to a CSV for subsequent
 * download.
 */
export const exportInventoryDevices = ({
  columns,
  augmenter,
  items,
  areTotalRowsSelected,
  showInventoryOnly,
  gridExportCount,
  ...rest
}: ExportClient) => {
  return async(dispatch: Function): Promise<any> => {
    const entity = ExportContext.inventoryDevices;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      const devices = (
        !items?.length
          ? await loadAllInventoryDevices({...rest, ...paging})
          : (areTotalRowsSelected && gridExportCount > paging?.pageSize)
            ? await loadAllInventoryDevices({...rest, ...paging})
            : items
      );
      exportCSV({
        includeTimestamp : true,
        items            : devices,
        entity,
        columns,
        augmenter
      });
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Job Search Engine Export Action Creator which manages retrieving
 * all Jobs, Task Status and Devices and converting the retrieved Items to a CSV
 * for subsequent download.
 */
export const exportJobSearchEngine = ({
  accountId,
  columns,
  sortOn,
  items,
  includeSubGroups = false
}) => {
  return async(dispatch: Function): Promise<any> => {
    const jobData          = [];
    const entity           = ExportContext.jobSearchEngine;
    const {tasks}          = items || {};
    const jobTabColumns    = getHeadersForTab(columns, JobDetails.JOBS);
    const taskTabColumns   = getHeadersForTab(columns, JobDetails.TASKS);
    const deviceTabColumns = getHeadersForTab(columns, JobDetails.DEVICES);

    dispatch({type: EXPORT_REQUEST, entity});

    const results = await loadAllData({
      api               : loadAllJobDevices,
      jobs              : items,
      isJobSearchEngine : true,
      accountId,
      sortOn,
      includeSubGroups
    });

    jobData.push({
      ...getJobDetails(items)
    });

    const flattenedResults = results?.flat();
    const taskData         = getTaskDetails(tasks);
    const deviceData       = getDeviceDetails(flattenedResults);

    try {
      exportXLSX({
        entity,
        jobData,
        taskData,
        deviceData,
        jobTabColumns,
        taskTabColumns,
        deviceTabColumns
      });
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

export const exportConfirmed = () => ({
  type : EXPORT_CONFIRMED
});

/**
 * Invoked when an export has completed and the user confirms acknowledgement.
 */
export const exportComplete = () => ({
  type : EXPORT_COMPLETED
});
